<script setup lang="ts">
import GaiaText from '../base/GaiaText.vue';
import { gaiaNeutral } from '../themes/tokens';

const props = defineProps({
  label: {
    type: String,
    required: true,
  },
  /** Secondary column, e.g. the AI question behind a custom column. */
  meta: {
    type: String,
    default: undefined,
  },
  /** Muted italic meta — "No question set yet" rather than a real one. */
  metaMuted: {
    type: Boolean,
    default: false,
  },
  /** Fixed rows can't be reordered or hidden: no grip, and a padlock instead. */
  locked: {
    type: Boolean,
    default: false,
  },
  /** Lifted while the pointer is dragging this row. */
  dragging: {
    type: Boolean,
    default: false,
  },
  /** The row the drop would land on. */
  dropTarget: {
    type: Boolean,
    default: false,
  },
  /** The row whose details are open beside the list (a master-detail list). */
  selected: {
    type: Boolean,
    default: false,
  },
});

/**
 * Reordering is the list's job, not the row's — the row renders the grip and
 * lets `dragstart` / `dragover` / `drop` fall through to the caller, the same
 * split the Kanban board uses for cards.
 */

const grip = gaiaNeutral.textMuted;
const lock = gaiaNeutral.textMuted;
const hover = gaiaNeutral.surfaceHover;
const active = gaiaNeutral.surfaceActive;
const dropBorder = gaiaNeutral.borderDashed;
</script>

<template>
  <!-- A row in a reorderable list: drag grip, name with badges, an optional
       second column, and trailing actions. Shared by the view's Columns
       popover and the create wizard's column list. -->
  <div
    class="gaia-reorder-row"
    :class="{ dragging: props.dragging, 'drop-target': props.dropTarget, selected: props.selected }"
    :draggable="!props.locked || undefined"
  >
    <span class="grip" :class="{ hidden: props.locked }" aria-hidden="true">
      <svg width="10" height="12" viewBox="0 0 10 12" fill="currentColor">
        <circle cx="3" cy="2.5" r="1.1" />
        <circle cx="7" cy="2.5" r="1.1" />
        <circle cx="3" cy="6" r="1.1" />
        <circle cx="7" cy="6" r="1.1" />
        <circle cx="3" cy="9.5" r="1.1" />
        <circle cx="7" cy="9.5" r="1.1" />
      </svg>
    </span>

    <span class="name">
      <gaia-text class="truncate" size="dense" weight="semibold" tone="ink">
        {{ props.label }}
      </gaia-text>
      <slot name="badges" />
      <svg
        v-if="props.locked"
        class="lock"
        width="11"
        height="11"
        viewBox="0 0 12 12"
        fill="none"
        stroke="currentColor"
        stroke-width="1.4"
      >
        <rect x="2.5" y="5.5" width="7" height="5" rx="1" />
        <path d="M4 5.5V4a2 2 0 0 1 4 0v1.5" />
      </svg>
    </span>

    <gaia-text
      v-if="props.meta"
      class="truncate meta"
      size="dense"
      :tone="props.metaMuted ? 'muted' : 'tertiary'"
      :style="{ fontStyle: props.metaMuted ? 'italic' : 'normal' }"
    >
      {{ props.meta }}
    </gaia-text>

    <span v-if="$slots.actions" class="actions"><slot name="actions" /></span>
  </div>
</template>

<style scoped>
.gaia-reorder-row {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 40px;
  padding: 0 10px;
  border-radius: 8px;
  border: 1px dashed transparent;
}

.gaia-reorder-row:hover {
  background: v-bind(hover);
}

.gaia-reorder-row.selected {
  background: v-bind(active);
}

.gaia-reorder-row.dragging {
  opacity: 0.45;
}

/* The dashed outline marks where the drop would land. */
.gaia-reorder-row.drop-target {
  border-color: v-bind(dropBorder);
}

.grip {
  flex: none;
  display: inline-flex;
  padding: 4px 2px;
  color: v-bind(grip);
  cursor: grab;
}

.grip.hidden {
  visibility: hidden;
  cursor: default;
}

.gaia-reorder-row:active .grip:not(.hidden) {
  cursor: grabbing;
}

.name {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
}

.lock {
  flex: none;
  color: v-bind(lock);
}

/* The second column takes the same share as the name, so questions line up
   down the list rather than starting wherever the name ended. */
.meta {
  flex: 1.3;
  min-width: 0;
}

.actions {
  margin-left: auto;
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

/* The ellipsis trio only works as a unit, and only while the flex parent keeps
   min-width:0 — so it stays one CSS idiom rather than becoming props. */
.truncate {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
